import { useEffect, useMemo, useState } from "react";
import {
  Archive, Award, Copy, Download, Edit2, Eye, EyeOff, FileText, History, Lock, Pencil, Plus,
  RotateCcw, Sparkles, Trash2, Unlock, FolderInput, Link2, Hash,
} from "lucide-react";
import { api, type AdminCourseExamItem, type AdminCourseListItem, type AdminCourseModuleItem } from "../../lib/api";
import { adminResourceOpsApi, type BulkOpAction, type HistoryEntry } from "../../lib/adminResourceOpsApi";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";
import { toast } from "../ui/Toast";
import { mapApiError } from "../ui/StatusMessage";
import { AdminDataTable, type Column, type RowAction } from "./dataTable/AdminDataTable";
import { BulkBar, type BulkAction } from "./dataTable/BulkBar";
import { FilterBar, type FilterDef } from "./dataTable/FilterBar";
import { useListState } from "./dataTable/useListState";

type Item = (AdminCourseModuleItem | AdminCourseExamItem) & { kind: "module" | "exam" };

type Props = {
  kind: "module" | "exam";
  items: (AdminCourseModuleItem | AdminCourseExamItem)[];
  token: string;
  courseId: string;
  isCoursePremium: boolean;
  isUnassigned: boolean;
  loading?: boolean;
  /** Reload the course after any change. */
  onChanged: () => void | Promise<void>;
  onEdit: (item: Item) => void;
  onPreview: (item: Item) => void;
  onGenerate?: (item: AdminCourseModuleItem) => void;
  onToggleVisibility: (item: Item) => void | Promise<void>;
  onToggleAccess: (item: Item) => void | Promise<void>;
  onDeleteForever: (item: Item) => void;
  onAdd?: () => void;
  addLabel: string;
};

function statusOf(i: Item): { label: string; variant: "success" | "warning" | "secondary" | "destructive" } {
  if (i.isArchived) return { label: "Archived", variant: "secondary" };
  if (!i.isPublished) return { label: "Draft", variant: "warning" };
  if (i.isHidden) return { label: "Hidden", variant: "secondary" };
  return { label: "Published", variant: "success" };
}

const STATUS_FILTER = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
  { value: "hidden", label: "Hidden" },
  { value: "archived", label: "Archived" },
];

export function CourseResourceTable(p: Props) {
  const list = useListState(p.kind === "module" ? "m" : "e", ["status", "access", "type"], { key: "date", dir: "desc" });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState<Item | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [moveIds, setMoveIds] = useState<string[] | null>(null);
  const [courses, setCourses] = useState<AdminCourseListItem[]>([]);
  const [targetCourse, setTargetCourse] = useState("");
  const [history, setHistory] = useState<{ item: Item; entries: HistoryEntry[] | null } | null>(null);

  const items = useMemo<Item[]>(() => p.items.map((i) => ({ ...i, kind: p.kind })), [p.items, p.kind]);

  const filtered = useMemo(() => {
    const q = list.q.trim().toLowerCase();
    return items.filter((i) => {
      if (q && !i.title.toLowerCase().includes(q)) return false;
      const st = statusOf(i).label.toLowerCase();
      if (list.filters.status && st !== list.filters.status) return false;
      if (!list.filters.status && i.isArchived) return false; // archived items only show under the Archived filter
      if (list.filters.access === "free" && i.isFree === false) return false;
      if (list.filters.access === "premium" && i.isFree !== false) return false;
      if (list.filters.type && (i as AdminCourseModuleItem).type?.toLowerCase() !== list.filters.type) return false;
      return true;
    });
  }, [items, list.q, list.filters]);

  // Drop selections that no longer exist (after reload).
  useEffect(() => {
    setSelected((prev) => {
      const ids = new Set(items.map((i) => i.id));
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [items]);

  const filters: FilterDef[] = [
    { key: "status", label: "Status", options: STATUS_FILTER },
    { key: "access", label: "Access", options: [{ value: "free", label: "Free" }, { value: "premium", label: "Premium" }] },
    ...(p.kind === "module"
      ? [{ key: "type", label: "Type", options: [{ value: "module", label: "Module" }, { value: "handout", label: "Handout" }] }]
      : []),
  ];

  async function run(fn: () => Promise<unknown>, okMsg?: string, undo?: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      if (okMsg) {
        toast.success(
          okMsg,
          undo
            ? {
                duration: 10000,
                action: {
                  label: "Undo",
                  onClick: () => {
                    void undo()
                      .then(() => p.onChanged())
                      .catch((e) => toast.error(mapApiError(e).message));
                  },
                },
              }
            : undefined,
        );
      }
      await p.onChanged();
    } catch (e) {
      toast.error(mapApiError(e).message);
    } finally {
      setBusy(false);
    }
  }

  const archive = (i: Item) =>
    run(() => adminResourceOpsApi.archive(p.token, i.id), `"${i.title}" archived`, () => adminResourceOpsApi.restore(p.token, i.id));

  async function openMove(ids: string[]) {
    setMoveIds(ids);
    setTargetCourse("");
    if (courses.length === 0) {
      try {
        const res = await api.admin.listCourses(p.token);
        setCourses(res.items.filter((c) => c.id !== p.courseId));
      } catch (e) {
        toast.error(mapApiError(e).message);
      }
    }
  }

  async function openHistory(i: Item) {
    setHistory({ item: i, entries: null });
    try {
      const res = await adminResourceOpsApi.history(p.token, i.id);
      setHistory({ item: i, entries: res.items });
    } catch (e) {
      setHistory(null);
      toast.error(mapApiError(e).message);
    }
  }

  const copy = (text: string, what: string) => {
    void navigator.clipboard?.writeText(text).then(
      () => toast.success(`${what} copied`),
      () => toast.error("Could not copy. Select and copy it manually."),
    );
  };

  const isRes = (i: Item) => i.kind === "module" || i.kind === "exam";
  const actions: RowAction<Item>[] = [
    { key: "preview", label: "Preview as student", icon: <Eye size={14} />, group: 0, quick: true, onSelect: p.onPreview, hidden: (i) => !i.studentUrl || !!i.isArchived },
    { key: "edit", label: "Edit details", icon: <Edit2 size={14} />, group: 0, quick: true, onSelect: p.onEdit, hidden: (i) => !!i.isArchived },
    { key: "rename", label: "Rename", icon: <Pencil size={14} />, group: 0, onSelect: (i) => { setRenaming(i); setNewTitle(i.title); }, hidden: (i) => !!i.isArchived },
    { key: "download", label: "Download file", icon: <Download size={14} />, group: 0, onSelect: (i) => window.open(i.rawUrl ?? "", "_blank", "noopener"), hidden: (i) => !i.rawUrl },
    { key: "generate", label: "Generate notes and quizzes", icon: <Sparkles size={14} />, group: 1, onSelect: (i) => p.onGenerate?.(i as AdminCourseModuleItem), hidden: (i) => !(p.onGenerate && i.kind === "module" && (i as AdminCourseModuleItem).type === "Module") || p.isUnassigned || !!i.isArchived },
    { key: "vis", label: "Hide / Show", icon: <EyeOff size={14} />, group: 1, onSelect: (i) => void p.onToggleVisibility(i), hidden: (i) => !i.isPublished || !!i.isArchived },
    { key: "access", label: "Switch Free / Premium", icon: <Lock size={14} />, group: 1, onSelect: (i) => void p.onToggleAccess(i), hidden: (i) => p.isCoursePremium || !!i.isArchived },
    { key: "dup", label: "Duplicate as draft", icon: <Copy size={14} />, group: 2, onSelect: (i) => void run(() => adminResourceOpsApi.duplicate(p.token, i.id), "Duplicate created as a draft"), hidden: (i) => !isRes(i) || !!i.isArchived },
    { key: "move", label: "Move to another course", icon: <FolderInput size={14} />, group: 2, onSelect: (i) => void openMove([i.id]), hidden: (i) => !!i.isArchived },
    { key: "history", label: "View history", icon: <History size={14} />, group: 3, onSelect: (i) => void openHistory(i) },
    { key: "link", label: "Copy student link", icon: <Link2 size={14} />, group: 3, onSelect: (i) => copy(`${window.location.origin}${i.studentUrl}`, "Link"), hidden: (i) => !i.studentUrl },
    { key: "id", label: "Copy ID", icon: <Hash size={14} />, group: 3, onSelect: (i) => copy(i.id, "ID") },
    { key: "restore", label: "Restore", icon: <RotateCcw size={14} />, group: 0, onSelect: (i) => void run(() => adminResourceOpsApi.restore(p.token, i.id), "Restored (hidden until you show it)"), hidden: (i) => !i.isArchived },
    { key: "archive", label: "Archive", icon: <Archive size={14} />, group: 4, danger: true, onSelect: (i) => void archive(i), hidden: (i) => !!i.isArchived },
    { key: "delete", label: "Delete permanently", icon: <Trash2 size={14} />, group: 4, danger: true, onSelect: p.onDeleteForever, hidden: (i) => !i.isArchived },
  ];

  const columns: Column<Item>[] = [
    { key: "status", header: "Status", className: "w-28 shrink-0", cell: (i) => { const s = statusOf(i); return <Badge variant={s.variant}>{s.label}</Badge>; } },
    {
      key: "access", header: "Access", className: "w-24 shrink-0", desktopOnly: true,
      cell: (i) => (p.isCoursePremium || i.isFree === false ? <Badge variant="warning"><Lock size={11} />Premium</Badge> : <Badge variant="info"><Unlock size={11} />Free</Badge>),
    },
    { key: "date", header: "Updated", className: "w-28 shrink-0", desktopOnly: true, sortValue: (i) => Date.parse(i.date) || 0, cell: (i) => <span className="text-muted-foreground">{i.date}</span> },
  ];

  const bulkActions: BulkAction[] = [
    { key: "show", label: "Show", icon: <Eye size={14} />, onClick: () => bulk("show") },
    { key: "hide", label: "Hide", icon: <EyeOff size={14} />, onClick: () => bulk("hide") },
    ...(!p.isCoursePremium
      ? [
          { key: "free", label: "Set free", icon: <Unlock size={14} />, onClick: () => bulk("make_free") },
          { key: "prem", label: "Set premium", icon: <Lock size={14} />, onClick: () => bulk("make_premium") },
        ]
      : []),
    { key: "move", label: "Move", icon: <FolderInput size={14} />, onClick: () => void openMove([...selected]) },
    { key: "restore", label: "Restore", icon: <RotateCcw size={14} />, onClick: () => bulk("restore") },
    { key: "archive", label: "Archive", icon: <Archive size={14} />, danger: true, onClick: () => bulk("archive") },
  ];

  async function bulk(action: BulkOpAction, courseId?: string) {
    const ids = [...selected];
    const label = action === "archive" ? "archived" : "updated";
    setBusy(true);
    try {
      const res = await adminResourceOpsApi.bulk(p.token, ids, action, courseId);
      const skipped = res.skipped.length ? ` ${res.skipped.length} skipped (drafts or archived items).` : "";
      toast.success(
        `${res.count} ${label}.${skipped}`,
        action === "archive"
          ? {
              duration: 10000,
              action: {
                label: "Undo",
                onClick: () => {
                  void adminResourceOpsApi.bulk(p.token, ids, "restore").then(() => p.onChanged());
                },
              },
            }
          : undefined,
      );
      setSelected(new Set());
      await p.onChanged();
    } catch (e) {
      toast.error(mapApiError(e).message);
    } finally {
      setBusy(false);
    }
  }

  const noun = p.kind === "module" ? "modules" : "exams";

  return (
    <div className="space-y-3">
      <FilterBar
        list={list}
        filters={filters}
        searchLabel={`Search ${noun}`}
        sortOptions={[{ key: "date", label: "Newest first", dir: "desc" }, { key: "date", label: "Oldest first", dir: "asc" }]}
      />
      <AdminDataTable<Item>
        rows={filtered}
        rowId={(i) => i.id}
        noun={noun}
        loading={p.loading}
        columns={columns}
        actions={actions}
        list={list}
        selected={selected}
        onSelectedChange={setSelected}
        onOpen={p.onEdit}
        primary={(i) => {
          if (i.isArchived) return { label: "Restore", onClick: () => void run(() => adminResourceOpsApi.restore(p.token, i.id), "Restored (hidden until you show it)") };
          if (!i.isPublished && !p.isUnassigned) {
            return {
              label: "Publish",
              onClick: () => void run(() => api.admin.approveCourseItem(p.token, p.courseId, i.kind === "exam" ? "exam" : (i as AdminCourseModuleItem).resourceType, i.id), "Published"),
            };
          }
          return null;
        }}
        titleCell={(i) => (
          <>
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${i.kind === "exam" ? "bg-info/10 text-info" : "bg-accent-soft text-primary"}`}>
              {i.kind === "exam" ? <Award size={16} /> : <FileText size={16} />}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-foreground">{i.title}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {i.kind === "exam"
                  ? `Year ${(i as AdminCourseExamItem).examYear} · ${(i as AdminCourseExamItem).questionCount} questions`
                  : `${(i as AdminCourseModuleItem).type} · ${(i as AdminCourseModuleItem).pageCount} pages`}
              </span>
            </span>
          </>
        )}
        empty={{
          title: `No ${noun} yet`,
          message: `Add your first ${p.kind === "module" ? "module" : "exam"} to this course.`,
          action: p.onAdd && !p.isUnassigned ? { label: p.addLabel, onClick: p.onAdd } : undefined,
        }}
      />
      <BulkBar count={selected.size} noun={noun} actions={bulkActions} busy={busy} onClear={() => setSelected(new Set())} />

      <Modal isOpen={Boolean(renaming)} onClose={() => setRenaming(null)} title="Rename" size="sm">
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            const target = renaming;
            if (!target || !newTitle.trim()) return;
            setRenaming(null);
            void run(() => adminResourceOpsApi.rename(p.token, target.id, newTitle.trim()), "Renamed");
          }}
        >
          <label htmlFor="rename-title" className="block text-sm font-semibold">Name</label>
          <input id="rename-title" autoFocus value={newTitle} onChange={(e) => setNewTitle(e.target.value)} maxLength={255} className="field w-full rounded-control px-3 py-2.5 text-base sm:text-sm" />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setRenaming(null)}>Cancel</Button>
            <Button type="submit" disabled={!newTitle.trim()}>Save</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={Boolean(moveIds)} onClose={() => setMoveIds(null)} title="Move to another course" size="sm">
        <div className="space-y-3">
          <label htmlFor="move-course" className="block text-sm font-semibold">Course</label>
          <select id="move-course" value={targetCourse} onChange={(e) => setTargetCourse(e.target.value)} className="field w-full rounded-control px-3 py-2.5 text-base sm:text-sm">
            <option value="">Choose a course</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>{c.code ? `${c.code} - ` : ""}{c.name}</option>
            ))}
          </select>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setMoveIds(null)}>Cancel</Button>
            <Button
              disabled={!targetCourse}
              loading={busy}
              onClick={() => {
                const ids = moveIds ?? [];
                setMoveIds(null);
                if (ids.length === 1) void run(() => adminResourceOpsApi.move(p.token, ids[0], targetCourse), "Moved");
                else { setSelected(new Set(ids)); void bulk("move", targetCourse); }
              }}
            >
              Move
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={Boolean(history)} onClose={() => setHistory(null)} title={history ? `History: ${history.item.title}` : "History"} size="md">
        {history?.entries === null ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : history?.entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No changes recorded yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {history?.entries?.map((h) => (
              <li key={h.id} className="py-2 text-sm">
                <span className="font-semibold text-foreground">{h.title}</span>
                <span className="text-muted-foreground"> · {h.by ?? "Admin"} · {h.at ? new Date(h.at).toLocaleString() : ""}</span>
              </li>
            ))}
          </ul>
        )}
      </Modal>

      {p.onAdd && !p.isUnassigned && items.length > 0 && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" icon={<Plus size={14} />} onClick={p.onAdd}>{p.addLabel}</Button>
        </div>
      )}
    </div>
  );
}
